import {
  Component,
  OnInit,
  signal,
  inject,
  OnDestroy,
  ViewChild,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { Menu } from 'primeng/menu';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';

import {
  SearchFormComponent,
  SearchFormData,
  FormFieldConfig,
} from '@shared/components/search-form/search-form.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import {
  ConfirmationDialogComponent,
  ConfirmationButton,
} from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import { PermissionService } from '@app/core/services/permission.service';
import { StockDeliveryService, StockDeliverySearchParams } from '../stock-delivery.service';
import { withPaginatedSearchFields } from '@shared/utils/paginated-field.helpers';
import {
  StockDeliveryRecord,
  STOCK_DELIVERY_SEARCH_CONFIG,
  STOCK_DELIVERY_TABLE_COLUMNS,
  STOCK_DELIVERY_DEFAULT_CONFIG,
  STOCK_DELIVERY_MESSAGES,
  STOCK_DELIVERY_DEFAULT_COLUMNS_ORDER,
  STOCK_DELIVERY_FLEXIBLE_COLUMNS_ORDER,
  STOCK_DELIVERY_ACTION_COLUMN,
} from '../stock-delivery.config';
import { BASE_PATH, OutboundDeliveryOrderControllerService, ResultRecordControllerService } from '@app/api';
import { environment } from '@environments/environment';
import {
  getDeliveryStatusLabel,
  getOutboundTypeLabel,
  getWarehouseOrStoreName,
} from '../delivery.helpers';
import { formatDateTimeWithLocale } from '@app/shared/utils/date-formatter.util';
import { openAdvancedTableColumnSelector } from '@shared/utils/advanced-table-column-selector.util';
import { mergeOneBasedPageFromPrimeEvent } from '@shared/utils/prime-paginator-one-based.util';
import { ErrorDialogService } from '@app/shared/services/error-dialog.service';
import { ErrorTranslatorService } from '@app/shared/services/error-translator-service';
import { extractErrorMessage } from '@app/shared/utils/error-handler.util';

// Action interface for outbound delivery actions
interface OutboundDeliveryAction {
  action: string;
  label: string;
  icon: string;
  severity: 'success' | 'info' | 'warning' | 'danger' | 'secondary';
  text: boolean;
  disabled?: boolean;
  visible?: boolean;
}

@Component({
  selector: 'app-stock-delivery-order',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    MenuModule,
    ToastModule,
    TranslateModule,
    SearchFormComponent,
    AdvancedDataTableComponent,
    ConfirmationDialogComponent,
    AppButtonComponent,
  ],
  providers: [
    MessageService,
    {
      provide: BASE_PATH,
      useValue: environment.stockURL,
    },
    ResultRecordControllerService,
    OutboundDeliveryOrderControllerService,
    StockDeliveryService,
  ],
  templateUrl: './stock-delivery-order.component.html',
  styleUrl: './stock-delivery-order.component.css',
})
export class StockDeliveryOrderComponent implements OnInit, OnDestroy {
  @ViewChild('actionMenu') actionMenu!: Menu;

  private router = inject(Router);
  private messageService = inject(MessageService);
  private stockDeliveryService = inject(StockDeliveryService);
  private translateService = inject(TranslateService);
  private readonly errorDialogService = inject(ErrorDialogService);
  private readonly errorTranslatorService = inject(ErrorTranslatorService);
  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );
  private destroy$ = new Subject<void>();

  // Permission flags - computed from PermissionService
  // Using specific submenu 'outbound' for inventory permissions
  canRead = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('inventory.outbound.view');
  });
  canCreate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('inventory.outbound.create');
  });
  canUpdate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('inventory.outbound.edit');
  });
  canDelete = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('inventory.outbound.delete');
  });
  canApprove = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('inventory.outbound.approve');
  });

  // Search form configuration - simplified with only: Mã Phiếu xuất hàng, loại phiếu xuất, trạng thái, kho xuất
  searchFormat = signal<FormFieldConfig[]>([]);

  // Check if user has any action permissions
  hasAnyActionPermission = computed(
    () =>
      this.canRead() ||
      this.canUpdate() ||
      this.canDelete() ||
      this.canApprove()
  );

  // Advanced table column configuration
  defaultColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      STOCK_DELIVERY_TABLE_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];
    for (const key of STOCK_DELIVERY_DEFAULT_COLUMNS_ORDER) {
      const col = fromConfig(key);
      if (col) {
        cols.push({
          ...col,
          label: this.translateService.instant(col.label),
        });
      }
    }

    // Always add actions column if user has any permission (at least read for view)
    if (this.hasAnyActionPermission()) {
      const actionsCol = fromConfig(STOCK_DELIVERY_ACTION_COLUMN.ACTIONS);
      if (actionsCol) {
        cols.push({
          ...actionsCol,
          label: this.translateService.instant(actionsCol.label),
        });
      }
    }

    return cols;
  });

  flexibleColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      STOCK_DELIVERY_TABLE_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];

    for (const prop of STOCK_DELIVERY_FLEXIBLE_COLUMNS_ORDER) {
      const col = fromConfig(prop);
      if (col) {
        cols.push({
          ...col,
          label: this.translateService.instant(col.label),
        });
      }
    }

    return cols;
  });

  // Data signals
  dataSource = signal<StockDeliveryRecord[]>([]);
  loading = signal(false);
  totalRecords = signal(0);
  searchParams = signal({
    page: 1, // Use 1-based pagination
    size: STOCK_DELIVERY_DEFAULT_CONFIG.PAGE_SIZE,
  });
  
  // Sort signals
  sortField = signal<string | undefined>(undefined);
  sortOrder = signal<1 | -1 | undefined>(undefined);

  // Flag to prevent double API calls
  private isSearching = false;

  // Menu signals
  currentMenuRecord = signal<StockDeliveryRecord | null>(null);
  currentMenuModel = signal<any[]>([]);

  // Reference to advanced table component
  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;

  // Dialog signals
  selectedOutboundOrder = signal<StockDeliveryRecord | null>(null);
  showApproveDialog = signal(false);
  showRejectDialog = signal(false);
  showStartPickingDialog = signal(false);
  showConfirmOutboundDialog = signal(false);
  showConfirmShippingDialog = signal(false);
  showCancelDialog = signal(false);
  showReadyToShipDialog = signal(false);
  selectedOutboundOrderForView = signal<StockDeliveryRecord | null>(null);

  // Configuration constants (for template access)
  readonly STOCK_DELIVERY_DEFAULT_CONFIG = STOCK_DELIVERY_DEFAULT_CONFIG;

  // Dialog button configurations
  approveDialogButtons: ConfirmationButton[] = [
    {
      label: 'outbound_management.dialog.actions.cancel',
      action: 'cancel',
      outlined: true,
    },
    {
      label: 'outbound_management.dialog.actions.approve',
      action: 'confirm',
      severity: 'info',
    },
  ];

  rejectDialogButtons: ConfirmationButton[] = [
    {
      label: 'outbound_management.dialog.actions.cancel',
      action: 'cancel',
      outlined: true,
    },
    {
      label: 'outbound_management.dialog.actions.reject',
      action: 'confirm',
      severity: 'danger',
    },
  ];

  startPickingDialogButtons: ConfirmationButton[] = [
    {
      label: 'outbound_management.dialog.actions.cancel',
      action: 'cancel',
      outlined: true,
    },
    {
      label: 'outbound_management.dialog.actions.start_picking',
      action: 'confirm',
      severity: 'info',
    },
  ];

  confirmShippingDialogButtons: ConfirmationButton[] = [
    {
      label: 'outbound_management.dialog.actions.cancel',
      action: 'cancel',
      outlined: true,
    },
    {
      label: 'outbound_management.dialog.actions.confirm_shipping',
      action: 'confirm',
      severity: 'success',
    },
  ];

  cancelDialogButtons: ConfirmationButton[] = [
    {
      label: 'outbound_management.dialog.actions.cancel',
      action: 'cancel',
      outlined: true,
    },
    {
      label: 'outbound_management.dialog.actions.confirm_cancel',
      action: 'confirm',
      severity: 'danger',
    },
  ];

  // Add new outbound order button
  onAddNew(): void {
    // Check permission before navigating
    if (!this.canCreate()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.permissions.no_permission'),
        detail: this.translateService.instant('common.permissions.no_permission'),
      });
      return;
    }
    this.router.navigate(['/stock/delivery/delivery-order/add']);
  }

  ngOnInit(): void {
    this.initializeSearchForm();
    this.initializeTableColumns();
    this.loadData();
    this.loadOptions();
  }

  private initializeSearchForm(): void {
    this.searchFormat.set(
      withPaginatedSearchFields(STOCK_DELIVERY_SEARCH_CONFIG, {
        outLogicWarehouseCode: {
          loadPage: (params) =>
            this.stockDeliveryService.getWarehouseOptionsPage(params),
        },
      }),
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeTableColumns(): void {
    // Column configurations are now computed signals, no need to set them here
  }

  onViewDetails(record: StockDeliveryRecord): void {
    // Check permission before navigating
    if (!this.canRead()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.permissions.no_permission'),
        detail: this.translateService.instant('common.permissions.no_permission'),
      });
      return;
    }

    if (!record || !record.id) {
      console.error('Invalid record for view:', record);
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.error'),
        detail: this.translateService.instant('outbound_management.messages.failed_to_load'),
      });
      return;
    }
    
    console.log('Navigating to view:', record.id, record);
    this.router.navigate(['/stock/delivery/delivery-order/view', record.id]).catch((error) => {
      console.error('Navigation error:', error);
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.error'),
        detail: 'Failed to navigate to view page',
      });
    });
  }

  /**
   * Get available actions for an outbound delivery order record
   * Show actions for transfer orders (transfer-out-stock) and purchase return orders (purchase-order-return-out-stock)
   * @param record - Outbound delivery order record
   * @returns Array of available actions based on status and permissions
   */
  getOutboundDeliveryActions(record: StockDeliveryRecord): OutboundDeliveryAction[] {
    const actions: OutboundDeliveryAction[] = [];

    // View action - only available if user has read permission
    if (this.canRead()) {
      actions.push({
        action: 'view',
        label: this.translateService.instant('common.actions.view'),
        icon: 'pi pi-eye',
        severity: 'info',
        text: true,
        visible: true,
      });
    }

    // Only show actions for transfer orders and purchase return orders
    if (record.outType !== 'transfer-out-stock' && record.outType !== 'purchase-order-return-out-stock') {
      return actions;
    }

    // Actions based on status for transfer orders
    switch (record.status) {
      case 'PENDING':
        // Approve action - requires canApprove permission
        if (this.canApprove()) {
          actions.push({
            action: 'approve',
            label: this.translateService.instant('outbound_management.actions.approve'),
            icon: 'pi pi-check',
            severity: 'success',
            text: true,
            visible: true,
          });
        }
        // Reject action - requires canApprove permission (reject is an approval decision)
        if (this.canApprove()) {
          actions.push({
            action: 'reject',
            label: this.translateService.instant('outbound_management.actions.reject'),
            icon: 'pi pi-times',
            severity: 'danger',
            text: true,
            visible: true,
          });
        }
        // Cancel action - requires canDelete permission
        if (this.canDelete()) {
          actions.push({
            action: 'cancel',
            label: this.translateService.instant('outbound_management.actions.cancel'),
            icon: 'pi pi-ban',
            severity: 'warning',
            text: true,
            visible: true,
          });
        }
        break;

      case 'APPROVED':
        // Start picking action - requires canUpdate permission
        if (this.canUpdate()) {
          actions.push({
            action: 'startPicking',
            label: this.translateService.instant('outbound_management.actions.start_picking'),
            icon: 'pi pi-play',
            severity: 'info',
            text: true,
            visible: true,
          });
        }
        // Ready to ship action - requires canUpdate permission
        if (this.canUpdate()) {
          actions.push({
            action: 'readyToShip',
            label: this.translateService.instant('outbound_management.actions.ready_to_ship'),
            icon: 'pi pi-check-circle',
            severity: 'info',
            text: true,
            visible: true,
          });
        }
        // Cancel action - requires canDelete permission
        if (this.canDelete()) {
          actions.push({
            action: 'cancel',
            label: this.translateService.instant('outbound_management.actions.cancel'),
            icon: 'pi pi-ban',
            severity: 'warning',
            text: true,
            visible: true,
          });
        }
        break;

      case 'PICKED':
        // Confirm shipping - requires canUpdate permission
        if (this.canUpdate()) {
          actions.push({
            action: 'confirmShipping',
            label: this.translateService.instant('outbound_management.actions.confirm_shipping'),
            icon: 'pi pi-truck',
            severity: 'info',
            text: true,
            visible: true,
          });
        }
        // Cancel action - requires canDelete permission
        if (this.canDelete()) {
          actions.push({
            action: 'cancel',
            label: this.translateService.instant('outbound_management.actions.cancel'),
            icon: 'pi pi-ban',
            severity: 'warning',
            text: true,
            visible: true,
          });
        }
        break;

    }

    return actions;
  }

  /**
   * Check if outbound delivery order has any enabled actions (excluding view)
   * @param record - Outbound delivery order record
   * @returns true if there are enabled actions
   */
  hasEnabledOutboundActions(record: StockDeliveryRecord): boolean {
    const actions = this.getOutboundDeliveryActions(record);
    return actions.some((action) => {
      if (action.action === 'view') return false; // View is handled separately
      return action.visible !== false;
    });
  }

  /**
   * Get actions model for PrimeNG menu
   * @param record - Outbound delivery order record
   * @returns Menu items array
   */
  getActionsModel(record?: StockDeliveryRecord): any[] {
    const targetRecord = record || this.currentMenuRecord();

    if (!targetRecord) {
      return [];
    }

    const actions = this.getOutboundDeliveryActions(targetRecord);

    // Filter out disabled actions and view action (view is shown separately)
    const menuItems = actions
      .filter((action) => action.action !== 'view' && action.visible !== false && !action.disabled)
      .map((action) => ({
        label: action.label,
        command: () => {
          this.onActionClick(action.action);
        },
      }));

    return menuItems;
  }

  /**
   * Handle action button clicks
   * @param action - Action type
   * @param record - Outbound delivery order record
   */
  onActionClick(action: string, record?: StockDeliveryRecord): void {
    const targetRecord = record || this.currentMenuRecord();

    if (!targetRecord) {
      console.warn('No record available for action:', action);
      return;
    }

    switch (action) {
      case 'view':
        this.onViewDetails(targetRecord);
        break;
      case 'approve':
        this.onApprove(targetRecord);
        break;
      case 'reject':
        this.onReject(targetRecord);
        break;
      case 'startPicking':
        this.onStartPicking(targetRecord);
        break;
      case 'confirmShipping':
        this.onConfirmShipping(targetRecord);
        break;
      case 'readyToShip':
        this.onReadyToShip(targetRecord);
        break;
      case 'cancel':
        this.onCancel(targetRecord);
        break;
      default:
        console.warn('Unknown action:', action);
    }
  }

  /**
   * Toggle menu for Others actions
   * @param event - Click event
   * @param record - Outbound delivery order record
   */
  toggleMenu(event: Event, record: StockDeliveryRecord): void {
    event.preventDefault();
    event.stopPropagation();

    // Set the current record and menu model
    this.currentMenuRecord.set(record);
    this.currentMenuModel.set(this.getActionsModel(record));

    // Toggle the menu using ViewChild reference
    if (this.actionMenu) {
      this.actionMenu.toggle(event);
    }
  }

  // Action handlers
  onApprove(record: StockDeliveryRecord): void {
    // Check permission before action
    if (!this.canApprove()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.permissions.no_permission'),
        detail: this.translateService.instant('common.permissions.no_permission'),
      });
      return;
    }
    this.selectedOutboundOrder.set(record);
    this.showApproveDialog.set(true);
  }

  onReject(record: StockDeliveryRecord): void {
    // Check permission before action - reject requires canApprove permission
    if (!this.canApprove()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.permissions.no_permission'),
        detail: this.translateService.instant('common.permissions.no_permission'),
      });
      return;
    }
    this.selectedOutboundOrder.set(record);
    this.showRejectDialog.set(true);
  }

  onStartPicking(record: StockDeliveryRecord): void {
    // Check permission before navigating
    if (!this.canUpdate()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.permissions.no_permission'),
        detail: this.translateService.instant('common.permissions.no_permission'),
      });
      return;
    }
    // Navigate to pick goods page (similar to receive goods page)
    this.router.navigate(['/stock/delivery/pick', record.id]);
  }

  onConfirmShipping(record: StockDeliveryRecord): void {
    // Check permission before navigating
    if (!this.canUpdate()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.permissions.no_permission'),
        detail: this.translateService.instant('common.permissions.no_permission'),
      });
      return;
    }
    // Navigate to confirm shipping page
    console.log('onConfirmShipping called with record:', record);
    if (record?.id) {
      console.log('Navigating to:', `/stock/delivery/confirm-shipping/${record.id}`);
      this.router.navigate(['/stock/delivery/confirm-shipping', record.id]);
    } else {
      console.error('Record ID is missing:', record);
    }
  }

  onCancel(record: StockDeliveryRecord): void {
    // Check permission before action - cancel requires canDelete permission
    if (!this.canDelete()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.permissions.no_permission'),
        detail: this.translateService.instant('common.permissions.no_permission'),
      });
      return;
    }
    this.selectedOutboundOrder.set(record);
    this.showCancelDialog.set(true);
  }

  onReadyToShip(record: StockDeliveryRecord): void {
    // Check permission before navigating
    if (!this.canUpdate()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.permissions.no_permission'),
        detail: this.translateService.instant('common.permissions.no_permission'),
      });
      return;
    }
    if (record?.id) {
      this.router.navigate(['/stock/delivery/confirm-shipping', record.id]);
    }
  }

  onReadyToShipConfirm(data: { deliveryDate?: string }): void {
    // Check permission before confirming
    if (!this.canUpdate()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.permissions.no_permission'),
        detail: this.translateService.instant('common.permissions.no_permission'),
      });
      this.showReadyToShipDialog.set(false);
      this.selectedOutboundOrderForView.set(null);
      return;
    }

    const order = this.selectedOutboundOrderForView();
    if (order) {
      // Call confirm-shipping API
      this.stockDeliveryService
        .confirmShipping(order.id, undefined, data.deliveryDate)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('common.messages.success'),
              detail: this.translateService.instant('outbound_management.messages.ready_to_ship_successfully'),
            });
            this.loadData();
            this.showReadyToShipDialog.set(false);
            this.selectedOutboundOrderForView.set(null);
          },
          error: (error) => {
            this.showOutboundApiError(
              error,
              'outbound_management.messages.failed_to_ready_to_ship',
            );
          },
        });
    }
  }

  onReadyToShipDialogClose(): void {
    this.showReadyToShipDialog.set(false);
    this.selectedOutboundOrderForView.set(null);
  }


  // Dialog confirm handlers
  onApproveConfirm(action: string): void {
    if (action === 'confirm') {
      // Check permission before confirming
      if (!this.canApprove()) {
        this.messageService.add({
          severity: 'warn',
          summary: this.translateService.instant('common.permissions.no_permission'),
          detail: this.translateService.instant('common.permissions.no_permission'),
        });
        this.showApproveDialog.set(false);
        this.selectedOutboundOrder.set(null);
        return;
      }

      const order = this.selectedOutboundOrder();
      if (order) {
        this.stockDeliveryService
          .updateOutboundStatus(order.id, 'APPROVED')
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.messageService.add({
                severity: 'success',
                summary: this.translateService.instant('common.messages.success'),
                detail: this.translateService.instant('outbound_management.messages.approved_successfully'),
              });
              this.loadData();
              this.showApproveDialog.set(false);
              this.selectedOutboundOrder.set(null);
            },
            error: (error) => {
              this.messageService.add({
                severity: 'error',
                summary: this.translateService.instant('common.messages.error'),
                detail: this.translateService.instant('outbound_management.messages.failed_to_approve'),
              });
            },
          });
      }
    } else {
      this.showApproveDialog.set(false);
      this.selectedOutboundOrder.set(null);
    }
  }

  onRejectConfirm(action: string): void {
    if (action === 'confirm') {
      // Check permission before confirming - reject requires canApprove permission
      if (!this.canApprove()) {
        this.messageService.add({
          severity: 'warn',
          summary: this.translateService.instant('common.permissions.no_permission'),
          detail: this.translateService.instant('common.permissions.no_permission'),
        });
        this.showRejectDialog.set(false);
        this.selectedOutboundOrder.set(null);
        return;
      }

      const order = this.selectedOutboundOrder();
      if (order) {
        // Reject action - set status to REJECT (status = 2: Failed to notify WMS)
        this.stockDeliveryService
          .updateOutboundStatus(order.id, 'REJECT')
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.messageService.add({
                severity: 'success',
                summary: this.translateService.instant('common.messages.success'),
                detail: this.translateService.instant('outbound_management.messages.rejected_successfully'),
              });
              this.loadData();
              this.showRejectDialog.set(false);
              this.selectedOutboundOrder.set(null);
            },
            error: (error) => {
              this.messageService.add({
                severity: 'error',
                summary: this.translateService.instant('common.messages.error'),
                detail: this.translateService.instant('outbound_management.messages.failed_to_reject'),
              });
            },
          });
      }
    } else {
      this.showRejectDialog.set(false);
      this.selectedOutboundOrder.set(null);
    }
  }

  onStartPickingConfirm(action: string): void {
    if (action === 'confirm') {
      // Check permission before confirming
      if (!this.canUpdate()) {
        this.messageService.add({
          severity: 'warn',
          summary: this.translateService.instant('common.permissions.no_permission'),
          detail: this.translateService.instant('common.permissions.no_permission'),
        });
        this.showStartPickingDialog.set(false);
        this.selectedOutboundOrder.set(null);
        return;
      }

      const order = this.selectedOutboundOrder();
      if (order) {
        this.stockDeliveryService
          .updateOutboundStatus(order.id, 'PICKING')
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.messageService.add({
                severity: 'success',
                summary: this.translateService.instant('common.messages.success'),
                detail: this.translateService.instant('outbound_management.messages.picking_started_successfully'),
              });
              this.loadData();
              this.showStartPickingDialog.set(false);
              this.selectedOutboundOrder.set(null);
            },
            error: (error) => {
              this.messageService.add({
                severity: 'error',
                summary: this.translateService.instant('common.messages.error'),
                detail: this.translateService.instant('outbound_management.messages.failed_to_start_picking'),
              });
            },
          });
      }
    } else {
      this.showStartPickingDialog.set(false);
      this.selectedOutboundOrder.set(null);
    }
  }
  onConfirmShippingConfirm(action: string): void {
    if (action === 'confirm') {
      // Check permission before confirming
      if (!this.canUpdate()) {
        this.messageService.add({
          severity: 'warn',
          summary: this.translateService.instant('common.permissions.no_permission'),
          detail: this.translateService.instant('common.permissions.no_permission'),
        });
        this.showConfirmShippingDialog.set(false);
        this.selectedOutboundOrder.set(null);
        return;
      }

      const order = this.selectedOutboundOrder();
      if (order) {
        this.stockDeliveryService
          .updateOutboundStatus(order.id, 'SHIPPED')
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.messageService.add({
                severity: 'success',
                summary: this.translateService.instant('common.messages.success'),
                detail: this.translateService.instant('outbound_management.messages.shipped_successfully'),
              });
              this.loadData();
              this.showConfirmShippingDialog.set(false);
              this.selectedOutboundOrder.set(null);
            },
            error: (error) => {
              this.showOutboundApiError(
                error,
                'outbound_management.messages.failed_to_confirm_shipping',
              );
            },
          });
      }
    } else {
      this.showConfirmShippingDialog.set(false);
      this.selectedOutboundOrder.set(null);
    }
  }

  /** Translate API errors (incl. CONCURRENT_MODIFICATION / HTTP 409) for outbound actions. */
  private showOutboundApiError(error: unknown, fallbackKey: string): void {
    this.displayOutboundApiError(error, fallbackKey);
  }

  private displayOutboundApiError(error: unknown, fallbackKey: string): void {
    const rawMessage = extractErrorMessage(error);
    const translated = this.errorTranslatorService.translate(
      rawMessage,
      fallbackKey,
    );
    const fallback = this.translateService.instant(fallbackKey);
    const detail =
      translated === fallback && /concurrent modification/i.test(rawMessage || '')
        ? rawMessage
        : translated;
    this.errorDialogService.showError(detail);
  }

  onCancelConfirm(action: string): void {
    if (action === 'confirm') {
      // Check permission before confirming - cancel requires canDelete permission
      if (!this.canDelete()) {
        this.messageService.add({
          severity: 'warn',
          summary: this.translateService.instant('common.permissions.no_permission'),
          detail: this.translateService.instant('common.permissions.no_permission'),
        });
        this.showCancelDialog.set(false);
        this.selectedOutboundOrder.set(null);
        return;
      }

      const order = this.selectedOutboundOrder();
      if (order) {
        this.stockDeliveryService
          .updateOutboundStatus(order.id, 'CANCELED')
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.messageService.add({
                severity: 'success',
                summary: this.translateService.instant('common.messages.success'),
                detail: this.translateService.instant('outbound_management.messages.cancelled_successfully'),
              });
              this.loadData();
              this.showCancelDialog.set(false);
              this.selectedOutboundOrder.set(null);
            },
            error: (error) => {
              this.messageService.add({
                severity: 'error',
                summary: this.translateService.instant('common.messages.error'),
                detail: this.translateService.instant('outbound_management.messages.failed_to_cancel'),
              });
            },
          });
      }
    } else {
      this.showCancelDialog.set(false);
      this.selectedOutboundOrder.set(null);
    }
  }

  onMenuHide(): void {
    this.currentMenuRecord.set(null);
  }

  onSearch(searchData: SearchFormData): void {
    this.currentMenuRecord.set(null);
    if (this.isSearching) return;

    this.isSearching = true;
    this.searchParams.set({ ...this.searchParams(), page: 1 });

    // Map search data to API params
    const searchParams: StockDeliverySearchParams = {
      queryCode: searchData['queryCode'],
      outType: searchData['outType'],
      status: searchData['status'],
      outLogicWarehouseCode: searchData['outLogicWarehouseCode'],
      beginCreationDate: searchData['beginCreationDate'],
      endCreationDate: searchData['endCreationDate'],
      page: 0,
      size: this.searchParams().size,
    };

    this.loadData(searchParams);
  }

  onReset(): void {
    this.currentMenuRecord.set(null);
    this.searchParams.set({
      page: 1,
      size: STOCK_DELIVERY_DEFAULT_CONFIG.PAGE_SIZE,
    });
    this.loadData();
  }

  onPageChange(event: any): void {
    this.currentMenuRecord.set(null);
    this.searchParams.set(
      mergeOneBasedPageFromPrimeEvent(this.searchParams(), event),
    );
    this.loadData();
  }

  onSortChange(event: { field: string; order: 1 | -1 }): void {
    this.sortField.set(event.field);
    this.sortOrder.set(event.order);
    this.searchParams.set({ ...this.searchParams(), page: 1 }); // Reset to first page when sorting
    this.loadData();
  }

  // Get status label for display
  getStatusLabel(status: string, outType?: string): string {
    return getDeliveryStatusLabel(status, this.translateService, true, outType);
  }

  // Get warehouse or store name (using helper)
  getWarehouseOrStoreName = getWarehouseOrStoreName;

  // Format receiver display - translate "Guest Customer" to Vietnamese
  getReceiverDisplay(receiver?: string): string {
    if (!receiver) {
      return '-';
    }
    if (receiver === 'Guest Customer') {
      return this.translateService.instant('outbound_management.guest_customer');
    }
    return receiver;
  }

  formatCreationTime(dateString: string): string {
    if (!dateString) return '-';

    return formatDateTimeWithLocale(dateString, 'vi-VN') || '-';
  }

  // Get outbound type label for display
  getOutboundTypeLabel(outType: string): string {
    return getOutboundTypeLabel(outType, this.translateService);
  }

  // Get reference code based on outType: orderCode for sales order, frontRecordCode for transfer order
  getReferenceCode(record: StockDeliveryRecord): string {
    if (!record) {
      return '-';
    }
    
    // If it's a sales order, use orderCode
    if (record.outType === 'order-sales-out-stock') {
      return record.orderCode || '-';
    }
    
    // If it's a transfer order, use frontRecordCode
    if (record.outType === 'transfer-out-stock') {
      return record.frontRecordCode || '-';
    }
    
    // Fallback to frontRecordCode if outType is not recognized
    return record.frontRecordCode || record.orderCode || '-';
  }

  // Refresh data
  refreshData(): void {
    this.searchParams.set({ ...this.searchParams(), page: 1 });
    this.loadData();
  }

  onColumnConfigClick(event: Event): void {
    openAdvancedTableColumnSelector(this.advancedTable, event);
  }

  private loadData(searchParams?: StockDeliverySearchParams): void {
    this.loading.set(true);

    const params: StockDeliverySearchParams = {
      page: this.searchParams().page - 1, // Convert 1-based to 0-based for API
      size: this.searchParams().size,
      sortBy: this.sortField(),
      ascending: this.sortOrder() === 1,
      ...searchParams,
    };

    this.stockDeliveryService
      .getDeliveryRecords(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.dataSource.set(response.data);
          this.totalRecords.set(response.totalRecords);
          this.loading.set(false);
          this.isSearching = false;
        },
        error: (error) => {
          console.error('Error loading delivery records:', error);
          this.loading.set(false);
          this.isSearching = false;
          
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: STOCK_DELIVERY_MESSAGES.ERROR.DATA_LOAD_FAILED,
          });
        },
      });
  }

  private loadOptions(): void {
    // Load status options with translations
    this.stockDeliveryService.getOutboundStatusOptions()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (options) => {
          const translatedOptions = options.map((option) => ({
            ...option,
            label: this.translateService.instant(option.label)
          }));

          const currentFormat = this.searchFormat();
          const updatedFormat = currentFormat.map((field) =>
            field.key === 'status'
              ? { ...field, options: translatedOptions }
              : field
          );
          
          this.searchFormat.set(updatedFormat);
          
          console.log('Status options loaded:', translatedOptions);
          console.log('Updated search format:', updatedFormat);
        },
        error: (error) => {
          console.error('Error loading status options:', error);
        }
      });

  }
}

